import type { IntakeSpec, PrintingEnclosureDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { HEAT_SET_DEFAULTS } from '@/lib/intakeBuilderDefaults';

function getDetails(intake: IntakeSpec): PrintingEnclosureDetails {
  return intake.details as PrintingEnclosureDetails;
}

export function parts(intake: IntakeSpec): Part[] {
  const dims = intake.core.size ?? {};
  const length = dims.length ?? 100;
  const width = dims.width ?? 80;
  const height = dims.height ?? 40;
  const wallT = 2.5;
  const floorT = 3;
  const lidT = 3;

  return [
    // Bottom panel
    {
      id: 'bottom',
      name: 'ボトム',
      kind: 'panel',
      materialId: 'mat-primary',
      width: length,
      depth: width,
      thickness: floorT,
      cutouts: [],
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      tags: ['enclosure'],
      paramRefs: [],
    },
    // Front wall
    {
      id: 'wall_front',
      name: '前面壁',
      kind: 'panel',
      materialId: 'mat-primary',
      width: length,
      depth: wallT,
      thickness: height - floorT,
      cutouts: [],
      position: [0, -(width / 2), (height - floorT) / 2 + floorT],
      rotation: [0, 0, 0],
      tags: ['enclosure'],
      paramRefs: [],
    },
    // Back wall
    {
      id: 'wall_back',
      name: '背面壁',
      kind: 'panel',
      materialId: 'mat-primary',
      width: length,
      depth: wallT,
      thickness: height - floorT,
      cutouts: [],
      position: [0, width / 2, (height - floorT) / 2 + floorT],
      rotation: [0, 0, 0],
      tags: ['enclosure'],
      paramRefs: [],
    },
    // Left wall
    {
      id: 'wall_left',
      name: '左側壁',
      kind: 'panel',
      materialId: 'mat-primary',
      width: wallT,
      depth: width - 2 * wallT,
      thickness: height - floorT,
      cutouts: [],
      position: [-(length / 2), 0, (height - floorT) / 2 + floorT],
      rotation: [0, 0, 0],
      tags: ['enclosure'],
      paramRefs: [],
    },
    // Right wall
    {
      id: 'wall_right',
      name: '右側壁',
      kind: 'panel',
      materialId: 'mat-primary',
      width: wallT,
      depth: width - 2 * wallT,
      thickness: height - floorT,
      cutouts: [],
      position: [length / 2, 0, (height - floorT) / 2 + floorT],
      rotation: [0, 0, 0],
      tags: ['enclosure'],
      paramRefs: [],
    },
    // Lid
    {
      id: 'lid',
      name: '蓋',
      kind: 'panel',
      materialId: 'mat-primary',
      width: length,
      depth: width,
      thickness: lidT,
      cutouts: [],
      position: [0, 0, height - lidT],
      rotation: [0, 0, 0],
      tags: ['enclosure', 'lid'],
      paramRefs: [],
    },
  ];
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const out: Feature[] = [];

  const dims = intake.core.size ?? {};
  const length = dims.length ?? 100;
  const width = dims.width ?? 80;
  const height = dims.height ?? 40;
  const lidT = 3;
  const wallT = 2.5;

  // Heat-set inserts on lid corners
  const it = d.insertType;
  if (it === 'heat_set_M2' || it === 'heat_set_M3' || it === 'heat_set_M4') {
    const sizeKey = it.replace('heat_set_', '') as 'M2' | 'M3' | 'M4';
    const ins = HEAT_SET_DEFAULTS[sizeKey];
    const inset = 8;
    const cornerPositions: Array<[number, number, number]> = [
      [-(length / 2 - inset), -(width / 2 - inset), height - lidT],
      [ (length / 2 - inset), -(width / 2 - inset), height - lidT],
      [-(length / 2 - inset),  (width / 2 - inset), height - lidT],
      [ (length / 2 - inset),  (width / 2 - inset), height - lidT],
    ];
    for (let i = 0; i < 4; i++) {
      out.push({
        id: `lid_boss_${i + 1}`,
        partId: 'lid',
        kind: 'boss',
        diameter: ins.bossOd,
        height: ins.bossHeight,
        axis: 'z',
        holeDiameter: ins.holeId,
        ribCount: 0,
        position: cornerPositions[i],
        rotation: [0, 0, 0],
        paramRefs: [],
      });
      out.push({
        id: `lid_insert_${i + 1}`,
        partId: 'lid',
        kind: 'insert_hole',
        insertType: 'heat_set',
        size: sizeKey,
        outerDiameter: ins.holeId,
        depth: ins.bossHeight - 1,
        axis: 'z',
        position: cornerPositions[i],
        rotation: [0, 0, 0],
        paramRefs: [],
      });
    }
  }

  // Vent grid on lid
  if (d.hasVents) {
    out.push({
      id: 'vent_grid',
      partId: 'lid',
      kind: 'vent_grid',
      rows: 5,
      columns: 8,
      pitchX: 8,
      pitchY: 8,
      holeDiameter: 4,
      depth: 3,
      axis: 'z',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  // Cable entry slot on front wall
  if (d.hasCableEntry) {
    out.push({
      id: 'cable_entry',
      partId: 'wall_front',
      kind: 'slot',
      length: 12,
      width: 8,
      depth: wallT,
      axis: 'y',
      through: true,
      roundedEnds: true,
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  // Edge chamfers on extreme quality
  if (intake.qualityLevel === 'extreme') {
    out.push({
      id: 'lid_edge_chamfer',
      partId: 'lid',
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 0.5,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
    out.push({
      id: 'bottom_edge_chamfer',
      partId: 'bottom',
      kind: 'edge_treatment',
      treatment: 'chamfer',
      amount: 0.5,
      scope: 'all',
      position: [0, 0, 0],
      rotation: [0, 0, 0],
      paramRefs: [],
    });
  }

  return out;
}
